/**
 * auth service api - points to auth.n450s.com (or localhost:3002/3045 in dev)
 *
 * OAUTH FLOW:
 *   - use redirectToLogin() or redirectToSignup() from utils/oauth.ts
 *   - these redirect to auth.n450s.com pages for unified SSO experience
 *   - callback handled by OAuthCallback component at /auth/callback
 *
 * DIRECT API ENDPOINTS:
 *   - account changes (username, email, password, MFA) call auth here with the Bearer token
 *   - sign-in and signup never do: they happen on the auth pages
 */
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { AUTH_URL } from "../../config/env";
import { applyAuthHeaders } from "../../utils/authHeaders";
import { createBaseQueryWithReauth } from "./baseQueryWithReauth";
import { tokenService } from "../tokenService";

// re-export OAuth utilities for convenience
export {
    redirectToLogin,
    redirectToSignup,
    logout as oauthLogout,
    getAuthServiceUrl,
} from "../../utils/oauth";

// base query for auth service
const baseQuery = fetchBaseQuery({
    baseUrl: AUTH_URL,
    credentials: 'include', // IMPORTANT: sends HttpOnly cookie for refresh token
    prepareHeaders: (headers) => applyAuthHeaders(headers),
});

// wrap with reauth logic (proactive + reactive token refresh)
const baseQueryWithReauth = createBaseQueryWithReauth({
    baseQuery,
});

// a step-up code or an email-change code allows 5 attempts. the reauth wrapper
// answers any 401 by refreshing and sending the request again, which would
// spend a second attempt on one press. the proactive refresh has just run, so a
// 401 on these requests means auth refused the code, and they go out once. if
// the token did lapse in between, the guard refuses before the code is checked
// and the next press works.
export function carriesOneTimeCode(args: string | FetchArgs): boolean {
    if (typeof args === "string" || !args.body || typeof args.body !== "object") {
        return false;
    }
    const body = args.body as Record<string, unknown>;
    return (
        typeof body.stepUpCode === "string" ||
        (typeof body.changeId === "string" && typeof body.code === "string")
    );
}

const authBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions,
) => {
    if (!carriesOneTimeCode(args)) {
        return baseQueryWithReauth(args, api, extraOptions);
    }
    await tokenService.getValidAccessToken();
    return baseQuery(args, api, extraOptions);
};

// interfaces
export type StepUpAction =
    | "change_username"
    | "change_email"
    | "set_password"
    | "change_password"
    | "disable_totp"
    | "delete_passkey"
    | "regenerate_recovery";

// the id and code of a step-up challenge (POST /auth/step-up) for one action
export interface StepUpProof {
    stepUpChallengeId: string;
    stepUpCode: string;
}

// what an account change sends as proof: the current password, or a step-up
// code for accounts that have no password
export interface AccountProof extends Partial<StepUpProof> {
    password?: string;
}

export interface StepUpChallenge {
    challengeId: string;
    sentTo: string;
    expiresInSeconds: number;
}

export interface RequestPasswordResetRequest {
    identifier: string;
}

export interface ResetPasswordRequest {
    resetToken: string;
    newPassword: string;
}

// with a password the proof is currentPassword; without one it is a
// set_password step-up
export interface ChangePasswordRequest extends Partial<StepUpProof> {
    currentPassword?: string;
    newPassword: string;
}

export interface ChangeUsernameRequest extends AccountProof {
    newUsername: string;
}

export interface UpdateEmailRequest extends AccountProof {
    newEmail: string;
}

// PUT /auth/email does not switch the address: it mails a code to the new one
// and answers 202 with changeId. an older auth answers with a message only
export interface EmailChangeStarted {
    changeId?: string;
    sentTo?: string;
    message?: string;
}

export interface ConfirmEmailChangeRequest {
    changeId: string;
    code: string;
}

export interface TotpSetupResponse {
    secret: string;
    qrCodeUri: string;
    manualEntryKey: string;
}

export interface PasskeyRegistrationOptions {
    options: PublicKeyCredentialCreationOptions;
}

export interface PasskeyAuthOptions {
    options: PublicKeyCredentialRequestOptions;
}

export interface Session {
    id: string;
    userAgent?: string;
    ipAddress?: string;
    deviceName?: string;
    lastActiveAt?: string;
    createdAt: string;
}

export interface UserInfo {
    id: string;
    userId: string;
    email: string;
    username: string;
    emailVerified: boolean;
    mfaVerified: boolean;
    role: string;
    // absent on an auth that predates passwordless accounts: treat as true
    hasPassword?: boolean;
    passwordLoginAvailable?: boolean;
}

// auth service api
export const authServiceApi = createApi({
    reducerPath: "authServiceApi",
    baseQuery: authBaseQuery,
    tagTypes: ["Auth", "MFA", "Sessions"],
    endpoints: (builder) => ({
        // ========== IDENTITY ==========
        logout: builder.mutation<void, void>({
            query: () => ({
                url: "/oauth/logout",
                method: "POST",
                // refresh token is in HttpOnly cookie, sent automatically with credentials: 'include'
            }),
        }),

        logoutAll: builder.mutation<void, void>({
            query: () => ({
                url: "/auth/logout/all",
                method: "POST",
            }),
            invalidatesTags: ["Sessions"],
        }),

        requestPasswordReset: builder.mutation<{ email: string }, RequestPasswordResetRequest>({
            query: (body) => ({
                url: "/auth/password/reset/request",
                method: "POST",
                body,
            }),
            transformResponse: (response: { data: { email: string } }) => response.data,
        }),

        resetPassword: builder.mutation<void, ResetPasswordRequest>({
            query: (body) => ({
                url: "/auth/password/reset",
                method: "POST",
                body,
            }),
        }),

        changePassword: builder.mutation<void, ChangePasswordRequest>({
            query: (body) => ({
                url: "/auth/password",
                method: "PUT",
                body,
            }),
        }),

        // called on auth directly, like password and email. the backend's
        // /api/profile/username only proxied here and demanded a password
        changeUsername: builder.mutation<void, ChangeUsernameRequest>({
            query: (body) => ({
                url: "/auth/username",
                method: "PUT",
                body,
            }),
        }),

        updateEmail: builder.mutation<EmailChangeStarted, UpdateEmailRequest>({
            query: (body) => ({
                url: "/auth/email",
                method: "PUT",
                body,
            }),
            transformResponse: (response: { data?: EmailChangeStarted } | undefined) => response?.data ?? {},
        }),

        confirmEmailChange: builder.mutation<{ signedOut: boolean }, ConfirmEmailChangeRequest>({
            query: (body) => ({
                url: "/auth/email/change/confirm",
                method: "POST",
                body,
            }),
            transformResponse: (response: { data: { signedOut: boolean } }) => response.data,
        }),

        getMe: builder.query<UserInfo, void>({
            query: () => "/auth/me",
            transformResponse: (response: { data: UserInfo }) => response.data,
            providesTags: ["Auth"],
        }),

        // ========== STEP-UP ==========
        requestStepUp: builder.mutation<StepUpChallenge, { action: StepUpAction }>({
            query: (body) => ({
                url: "/auth/step-up",
                method: "POST",
                body,
            }),
            transformResponse: (response: { data: StepUpChallenge }) => response.data,
        }),

        // ========== MFA - TOTP ==========
        setupTotp: builder.mutation<TotpSetupResponse, void>({
            query: () => ({
                url: "/auth/mfa/totp/setup",
                method: "POST",
            }),
            transformResponse: (response: { data: TotpSetupResponse }) => response.data,
        }),

        enableTotp: builder.mutation<void, { code: string }>({
            query: (body) => ({
                url: "/auth/mfa/totp/enable",
                method: "POST",
                body,
            }),
            invalidatesTags: ["MFA"],
        }),

        disableTotp: builder.mutation<void, AccountProof>({
            query: (body) => ({
                url: "/auth/mfa/totp",
                method: "DELETE",
                body,
            }),
            invalidatesTags: ["MFA"],
        }),

        // ========== MFA - PASSKEYS ==========
        getPasskeyRegistrationOptions: builder.mutation<PasskeyRegistrationOptions, void>({
            query: () => ({
                url: "/auth/mfa/passkey/register/options",
                method: "POST",
            }),
            transformResponse: (response: { data: PasskeyRegistrationOptions }) => response.data,
        }),

        verifyPasskeyRegistration: builder.mutation<void, { credential: unknown; deviceName?: string }>({
            query: (body) => ({
                url: "/auth/mfa/passkey/register/verify",
                method: "POST",
                body,
            }),
            invalidatesTags: ["MFA"],
        }),

        listPasskeys: builder.query<{ id: string; deviceName: string; createdAt: string; lastUsedAt?: string }[], void>({
            query: () => "/auth/mfa/passkeys",
            transformResponse: (response: { data: { id: string; deviceName: string; createdAt: string; lastUsedAt?: string }[] }) => response.data,
            providesTags: ["MFA"],
        }),

        deletePasskey: builder.mutation<void, { passkeyId: string } & AccountProof>({
            query: ({ passkeyId, ...proof }) => ({
                url: `/auth/mfa/passkey/${passkeyId}`,
                method: "DELETE",
                body: proof,
            }),
            invalidatesTags: ["MFA"],
        }),

        // ========== MFA - EMAIL ==========
        sendEmailCode: builder.mutation<{ sent: boolean; expiresIn: number }, void>({
            query: () => ({
                url: "/auth/mfa/email/send",
                method: "POST",
            }),
            transformResponse: (response: { data: { sent: boolean; expiresIn: number } }) => response.data,
        }),

        verifyEmailCode: builder.mutation<{ verified: boolean }, { code: string }>({
            query: (body) => ({
                url: "/auth/mfa/email/verify",
                method: "POST",
                body,
            }),
            transformResponse: (response: { data: { verified: boolean } }) => response.data,
        }),

        disableEmailMfa: builder.mutation<void, void>({
            query: () => ({
                url: "/auth/mfa/email",
                method: "DELETE",
            }),
            invalidatesTags: ["MFA"],
        }),

        // ========== MFA - RECOVERY CODES ==========
        getRecoveryCount: builder.query<{ count: number }, void>({
            query: () => "/auth/mfa/recovery/count",
            transformResponse: (response: { data: { count: number } }) => response.data,
            providesTags: ["MFA"],
        }),

        regenerateRecoveryCodes: builder.mutation<{ codes: string[] }, AccountProof>({
            query: (body) => ({
                url: "/auth/mfa/recovery/regenerate",
                method: "POST",
                body,
            }),
            transformResponse: (response: { data: { codes: string[] } }) => response.data,
            invalidatesTags: ["MFA"],
        }),

        // ========== SESSIONS ==========
        listSessions: builder.query<Session[], void>({
            query: () => "/auth/sessions",
            transformResponse: (response: { data: Session[] }) => response.data,
            providesTags: ["Sessions"],
        }),

        revokeSession: builder.mutation<void, string>({
            query: (sessionId) => ({
                url: `/auth/sessions/${sessionId}`,
                method: "DELETE",
            }),
            invalidatesTags: ["Sessions"],
        }),

        revokeAllSessions: builder.mutation<void, void>({
            query: () => ({
                url: "/auth/sessions",
                method: "DELETE",
            }),
            invalidatesTags: ["Sessions"],
        }),
    }),
});

export const {
    useLogoutMutation,
    useLogoutAllMutation,
    useRequestPasswordResetMutation,
    useResetPasswordMutation,
    useChangePasswordMutation,
    useChangeUsernameMutation,
    useUpdateEmailMutation,
    useConfirmEmailChangeMutation,
    useGetMeQuery,
    useRequestStepUpMutation,
    useSetupTotpMutation,
    useEnableTotpMutation,
    useDisableTotpMutation,
    useGetPasskeyRegistrationOptionsMutation,
    useVerifyPasskeyRegistrationMutation,
    useListPasskeysQuery,
    useDeletePasskeyMutation,
    useSendEmailCodeMutation,
    useVerifyEmailCodeMutation,
    useDisableEmailMfaMutation,
    useGetRecoveryCountQuery,
    useRegenerateRecoveryCodesMutation,
    useListSessionsQuery,
    useRevokeSessionMutation,
    useRevokeAllSessionsMutation,
} = authServiceApi;
